import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { toEmbedSrc, embedKind } from "@/lib/embeds";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * After Dark — the hidden channel at /after-dark.
 * Lives INSIDE the standard Layout (Header/Footer inherited) so page margins
 * stay consistent with the rest of the site. Uses the same section-header
 * typography pattern as SceneSection (kicker · big title · italic subtitle · body).
 *
 * Admin can control layout via site_content:
 *   after_dark_columns    → "1" | "2" | "3" (default 2)
 *   after_dark_container  → narrow / default / wide / full
 */
export default function AfterDark() {
    const { lang } = useT();
    const { content: siteContent } = useSiteContent();
    const [items, setItems] = useState([]);
    const [coupon, setCoupon] = useState(null);
    const [intro, setIntro] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/after-dark")
            .then((r) => {
                setItems(Array.isArray(r.data?.items) ? r.data.items : []);
                setCoupon(r.data?.coupon || null);
                setIntro(r.data?.intro || {});
            })
            .catch(() => { /* silent */ })
            .finally(() => setLoading(false));
    }, []);

    const kicker = intro[`kicker_${lang}`] || intro.kicker_en || "hidden channel";
    const title = intro[`title_${lang}`] || intro.title_en || "After Dark";
    const subtitle = intro[`subtitle_${lang}`] || intro.subtitle_en || "";
    const introText = intro[`intro_${lang}`] || intro.intro_en || "";

    const featured = useMemo(() => items.filter((i) => i.featured), [items]);
    const rest = useMemo(() => items.filter((i) => !i.featured), [items]);

    // Admin-driven layout — same pattern as SceneSection so admins have one mental model.
    const COLS = {
        "1": "grid-cols-1 gap-6 md:gap-10",
        "2": "grid-cols-1 md:grid-cols-2 gap-6 md:gap-8",
        "3": "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8",
    };
    const CONTAINERS = {
        narrow:  "max-w-3xl",
        default: "max-w-5xl",
        wide:    "max-w-6xl",
        full:    "max-w-none",
    };
    const gridCls = COLS[String(siteContent.after_dark_columns || "2")] || COLS["2"];
    const containerCls = CONTAINERS[siteContent.after_dark_container || "default"] || CONTAINERS.default;

    return (
        <div data-testid="after-dark-page">
            {/* Ambient grain — fixed layer for the "hidden place" feel */}
            <div
                aria-hidden="true"
                className="fixed inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "3px 3px",
                }}
            />

            {/* Hero — same padding pattern as SceneSection so margins line up. */}
            <section className="px-4 md:px-8 pt-14 md:pt-20 pb-10 md:pb-16 border-b border-current/10">
                <div className={`${containerCls} mx-auto space-y-4`}>
                    <div className="font-mono text-sm md:text-base uppercase tracking-[0.35em] text-[color:var(--sc-accent)]">
                        {kicker}
                    </div>
                    <h1 className="itz-display font-heading font-black uppercase tracking-tight leading-[0.9]">
                        {title.replace(/\.$/, "")}<span className="text-[color:var(--sc-accent)]">.</span>
                    </h1>
                    {subtitle && (
                        <p className="font-body italic text-lg md:text-2xl leading-snug opacity-80">
                            {subtitle}
                        </p>
                    )}
                    {introText && (
                        <p className="font-body text-sm md:text-base leading-relaxed opacity-70 whitespace-pre-line max-w-2xl pt-2">
                            {introText}
                        </p>
                    )}
                </div>
            </section>

            {coupon && <CouponBanner coupon={coupon} lang={lang} containerCls={containerCls} />}

            {/* Featured */}
            {featured.length > 0 && (
                <section className="px-4 md:px-8 py-10 md:py-14">
                    <div className={`${containerCls} mx-auto`}>
                        <SectionKicker kicker={lang === "es" ? "en el foco" : "in focus"} />
                        <div className={`grid ${gridCls}`}>
                            {featured.map((it) => (
                                <AfterDarkCard key={it.id} item={it} lang={lang} featured />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Rest of feed */}
            <section className="px-4 md:px-8 py-10 md:py-14 border-t border-current/10">
                <div className={`${containerCls} mx-auto`}>
                    <SectionKicker kicker={lang === "es" ? "el archivo" : "the archive"} />
                    {loading ? (
                        <p className="font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-40">loading…</p>
                    ) : rest.length === 0 && featured.length === 0 ? (
                        <p className="font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-40">
                            {lang === "es" ? "nada nuevo por ahora. volvé pronto." : "nothing new for now. come back soon."}
                        </p>
                    ) : (
                        <div className={`grid ${gridCls}`}>
                            {rest.map((it) => (
                                <AfterDarkCard key={it.id} item={it} lang={lang} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Signature */}
            <section className="px-4 md:px-8 py-10 border-t border-current/10">
                <div className={`${containerCls} mx-auto`}>
                    <p className="font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-40">
                        itzkatu · after dark · {new Date().getFullYear()}
                    </p>
                    <Link
                        to="/"
                        data-testid="after-dark-home-link"
                        className="mt-3 inline-flex items-center gap-2 font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-50 hover:opacity-100 transition-opacity"
                    >
                        ← itzkatu.studio
                    </Link>
                </div>
            </section>
        </div>
    );
}

function SectionKicker({ kicker }) {
    return (
        <div className="mb-6 md:mb-8">
            <div className="font-mono text-sm md:text-base uppercase tracking-[0.35em] opacity-60">{kicker}</div>
        </div>
    );
}

function CouponBanner({ coupon, lang, containerCls }) {
    const [copied, setCopied] = useState(false);
    const label = coupon[`label_${lang}`] || coupon.label_en || "a small gift";
    const desc = coupon[`description_${lang}`] || coupon.description_en || "";
    const onCopy = () => {
        navigator.clipboard.writeText(coupon.code || "").catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
    };
    return (
        <section data-testid="after-dark-coupon" className="px-4 md:px-8 py-8 md:py-10 border-y" style={{ borderColor: "var(--sc-accent)", backgroundColor: "var(--sc-card)" }}>
            <div className={`${containerCls} mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-5`}>
                <div className="space-y-2">
                    <div className="font-mono text-sm md:text-base uppercase tracking-[0.35em] text-[color:var(--sc-accent)]">
                        {lang === "es" ? "regalo temporal" : "temporary gift"}
                    </div>
                    <h3 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        {label}
                    </h3>
                    {desc && <p className="max-w-xl font-body italic text-lg md:text-2xl leading-snug opacity-80">{desc}</p>}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onCopy}
                        data-testid="after-dark-coupon-copy"
                        className="inline-flex items-center gap-2 px-4 py-3 font-mono text-sm md:text-base uppercase tracking-[0.22em] transition-colors"
                        style={{ backgroundColor: "var(--sc-accent)", color: "var(--sc-bg)" }}
                    >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {coupon.code}
                    </button>
                    {coupon.url && (
                        <a
                            href={coupon.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-70 hover:opacity-100 transition-opacity"
                        >
                            {lang === "es" ? "usar" : "use"} <ArrowUpRight size={14} />
                        </a>
                    )}
                </div>
            </div>
        </section>
    );
}

// --------------- Card ---------------
// Zero padding — media block sits edge-to-edge inside the card; only the
// text block below the media gets breathing room. Matches the reference
// layout the user provided.
function AfterDarkCard({ item, lang, featured = false }) {
    const {
        kind = "audio",
        title,
        subtitle,
        description,
        embed_url,
        media_url,
        image_url,
        external_url,
        artist_slug,
        tags = [],
    } = item;
    const localizedTitle = item[`title_${lang}`] || item.title_en || title || "";
    const localizedSubtitle = item[`subtitle_${lang}`] || item.subtitle_en || subtitle || "";
    const localizedDescription = item[`description_${lang}`] || item.description_en || description || "";
    const embed = (embed_url || "").trim();
    const eKind = embedKind(embed);
    const showEmbed = embed && ["audio", "video", "vertical-video", "photo"].includes(eKind);

    const kindLabels = {
        audio: "mix",
        video: "video",
        reel: "reel",
        photo: lang === "es" ? "foto" : "photo",
        wallpaper: "wallpaper",
        zine: "zine",
        download: lang === "es" ? "descarga" : "download",
        story: lang === "es" ? "historia" : "story",
        embed: "embed",
        link: "link",
    };
    const kindLabel = kindLabels[kind] || kind;
    const isDownloadKind = ["wallpaper", "zine", "download"].includes(kind);

    return (
        <article
            data-testid={`after-dark-card-${item.id}`}
            className="group relative flex flex-col overflow-hidden"
            style={{ backgroundColor: "var(--sc-card)" }}
        >
            {/* Media — edge-to-edge, no card padding. */}
            {(showEmbed || image_url) && (
                <div className="w-full">
                    {showEmbed ? (
                        <EmbedBlock url={embed} kind={eKind} title={title} />
                    ) : image_url ? (
                        <div className="w-full aspect-square bg-black overflow-hidden">
                            <img
                                src={image_url}
                                alt={title}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ) : null}
                </div>
            )}

            {/* Text block — padding lives HERE, not on the card wrapper. */}
            <div className={`flex flex-col ${featured ? "p-6 md:p-8" : "p-5 md:p-6"} space-y-2`}>
                <div className="flex items-baseline justify-between gap-3">
                    <div className="font-mono text-sm md:text-base uppercase tracking-[0.35em] text-[color:var(--sc-accent)]">
                        {kindLabel}
                    </div>
                    {tags.length > 0 && (
                        <div className="font-mono text-sm md:text-base uppercase tracking-[0.3em] opacity-40 truncate">
                            {tags.slice(0, 2).join(" · ")}
                        </div>
                    )}
                </div>

                <h3 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
                    {localizedTitle}
                </h3>
                {localizedSubtitle && (
                    <div className="font-body italic text-lg md:text-2xl leading-snug opacity-70">
                        {localizedSubtitle}
                    </div>
                )}
                {localizedDescription && (
                    <p className="font-body text-sm md:text-base leading-relaxed opacity-70 whitespace-pre-line pt-2">
                        {localizedDescription}
                    </p>
                )}

                {(media_url || (external_url && !media_url) || artist_slug || isDownloadKind) && (
                    <div className="pt-4 flex flex-wrap items-center gap-3">
                        {media_url && (
                            <a
                                href={media_url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`after-dark-download-${item.id}`}
                                className="inline-flex items-center gap-2 px-4 py-3 font-mono text-sm md:text-base uppercase tracking-[0.22em] transition-colors"
                                style={{ backgroundColor: "var(--sc-accent)", color: "var(--sc-bg)" }}
                            >
                                <Download size={16} />
                                {lang === "es" ? "descargar" : "download"}
                            </a>
                        )}
                        {external_url && !media_url && (
                            <a
                                href={external_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 font-mono text-sm md:text-base uppercase tracking-[0.22em] opacity-90 hover:opacity-100 hover:text-[color:var(--sc-accent)] transition-colors"
                            >
                                {lang === "es" ? "abrir" : "open"} <ArrowUpRight size={14} />
                            </a>
                        )}
                        {artist_slug && (
                            <Link
                                to={`/artists/${artist_slug}`}
                                className="ml-auto font-mono text-sm md:text-base uppercase tracking-[0.22em] opacity-60 hover:opacity-100 transition-opacity"
                            >
                                {lang === "es" ? "más sobre el artista" : "more from artist"} →
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}

function EmbedBlock({ url, kind, title }) {
    const src = toEmbedSrc(url);
    const isInstagram = /instagram\.com/i.test(url);
    const isSpotifyAlbum = /spotify\.com\/(album|playlist)/i.test(url);
    if (isInstagram) {
        return (
            <iframe
                title={title || "embed"}
                src={src}
                width="100%"
                height="880"
                className="block w-full bg-black"
                frameBorder="0"
                scrolling="no"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                loading="lazy"
            />
        );
    }
    if (isSpotifyAlbum) {
        return (
            <iframe
                title={title || "embed"}
                src={src}
                width="100%"
                height="500"
                className="block w-full bg-black"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
            />
        );
    }
    if (kind === "audio") {
        return (
            <iframe
                title={title || "embed"}
                src={src}
                width="100%"
                height="232"
                className="block w-full bg-black"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
            />
        );
    }
    if (kind === "vertical-video") {
        return (
            <div className="w-full max-w-[280px] aspect-[9/16] bg-black overflow-hidden mx-auto">
                <iframe
                    title={title || "embed"}
                    src={src}
                    className="block w-full h-full"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                    loading="lazy"
                />
            </div>
        );
    }
    if (kind === "photo") {
        return (
            <div className="w-full aspect-square bg-black overflow-hidden">
                <iframe
                    title={title || "embed"}
                    src={src}
                    className="block w-full h-full"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    loading="lazy"
                />
            </div>
        );
    }
    return (
        <div className="w-full aspect-video bg-black overflow-hidden">
            <iframe
                title={title || "embed"}
                src={src}
                className="block w-full h-full"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                loading="lazy"
            />
        </div>
    );
}
